'use client';

import { useEffect, useRef } from 'react';

/**
 * Fills the template's `[data-countdown]` element.
 *
 * The source design ships an empty div and lets a jQuery plugin write the digits in. That
 * plugin was not vendored, so the port rendered the labels with no numbers. This reproduces
 * the same markup the plugin emits, driven by the target date already on the attribute.
 */
export function LandingCountdown() {
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    const el = document.querySelector<HTMLElement>('[data-countdown]');
    if (!el) return;

    const target = Date.parse((el.dataset.countdown ?? '').replace(/-/g, '/'));
    if (Number.isNaN(target)) return;

    const UNITS: Array<[label: string, ms: number]> = [
      ['days', 86_400_000],
      ['hours', 3_600_000],
      ['mins', 60_000],
      ['secs', 1000],
    ];

    const render = () => {
      let left = Math.max(0, target - Date.now());
      // Reproduce the plugin's own markup exactly, the template CSS targets
      // .countdown-section / .countdown-amount / .countdown-period, so anything else
      // renders unstyled (which is why the block collapsed to 27px instead of 222px).
      el.replaceChildren(
        ...UNITS.map(([label, ms]) => {
          const value = Math.floor(left / ms);
          left -= value * ms;
          const section = document.createElement('span');
          section.className = 'countdown-section';
          const amount = document.createElement('span');
          amount.className = 'countdown-amount';
          amount.textContent = String(value);
          const period = document.createElement('span');
          period.className = 'countdown-period';
          period.textContent = ` ${label} `;
          section.append(amount, period);
          return section;
        })
      );
    };

    render();
    const tick = window.setInterval(render, 1000);
    return () => {
      window.clearInterval(tick);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, []);

  return null;
}
